import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Search, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, downloadCsv, formatError, DAYS, DAY_SHORT, ROLES } from "@/lib/api";
import { PageTitle } from "./AdminApp";

const FILTER_OPTIONS = {
  day: { label: "Day", options: DAYS.map((d) => [d, DAY_SHORT[d]]) },
  opt_in: { label: "Opt-in", options: [["true", "Opted in"], ["false", "Not opted in"]] },
  volunteer: { label: "Volunteer", options: [["true", "Volunteering"], ["false", "Not volunteering"]] },
  status: { label: "Status", options: [["pending_payment", "Pending payment"], ["paid", "Paid"]] },
  role: { label: "Role", options: ROLES.map((r) => [r.value, r.label]) },
  source: { label: "Source", options: [["donation", "Donation"], ["manual", "Manual / free"]] },
  vendor_status: { label: "Status", param: "status", options: [["new", "New"], ["contacted", "Contacted"], ["approved", "Approved"], ["declined", "Declined"]] },
};

const FilterSelect = ({ name, value, onChange }) => {
  const cfg = FILTER_OPTIONS[name];
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger className="h-10 w-[150px] border-neutral-800 bg-neutral-900" data-testid={`filter-${name}`}>
        <SelectValue placeholder={cfg.label} />
      </SelectTrigger>
      <SelectContent className="border-neutral-800 bg-neutral-900">
        <SelectItem value="all">All {cfg.label.toLowerCase()}{cfg.label.endsWith("s") ? "es" : "s"}</SelectItem>
        {cfg.options.map(([v, l]) => (
          <SelectItem key={v} value={v} data-testid={`filter-${name}-option-${v}`}>{l}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const ConfirmDelete = ({ onConfirm, testid }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button className="rounded p-1.5 text-neutral-500 transition-colors hover:bg-red-950 hover:text-red-400" data-testid={testid} title="Delete">
        <Trash2 className="h-4 w-4" />
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent className="border-neutral-800 bg-neutral-950">
      <AlertDialogHeader>
        <AlertDialogTitle>Delete this record?</AlertDialogTitle>
        <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-red-700 text-white hover:bg-red-600" data-testid="confirm-delete-ok">Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default function RecordsTable({ name, title, columns, filters = [], actions, headerExtra, reloadKey = 0 }) {
  const [params, setParams] = useState({ q: "", date_from: "", date_to: "" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  const clean = useCallback(() => Object.fromEntries(Object.entries(params).filter(([, v]) => v)), [params]);

  useEffect(() => {
    const id = setTimeout(() => {
      setLoading(true);
      api.get(`/admin/${name}`, { params: clean() })
        .then((r) => setItems(r.data.items))
        .catch((e) => toast.error(formatError(e)))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(id);
  }, [name, clean, tick, reloadKey]);

  const set = (k) => (v) => setParams((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const exportCsv = () =>
    downloadCsv(`/admin/${name}/export`, clean(), `${name}-${new Date().toISOString().slice(0, 10)}.csv`)
      .then(() => toast.success("CSV downloaded"))
      .catch((e) => toast.error(formatError(e)));
  const remove = async (id) => {
    try {
      await api.delete(`/admin/${name}/${id}`);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <div data-testid={`records-${name}`}>
      <PageTitle title={title}>
        {headerExtra}
        <button onClick={exportCsv} className="flex h-10 items-center gap-2 rounded-full border border-amber-500/50 px-4 text-sm font-semibold text-amber-400 transition-colors hover:bg-amber-500 hover:text-neutral-950" data-testid={`export-${name}-csv`}>
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </PageTitle>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
          <Input value={params.q} onChange={set("q")} placeholder="Search name, email, phone…" className="h-10 border-neutral-800 bg-neutral-900 pl-9" data-testid={`search-${name}`} />
        </div>
        {filters.map((f) => {
          const key = FILTER_OPTIONS[f].param || f;
          return <FilterSelect key={f} name={f} value={params[key]} onChange={set(key)} />;
        })}
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <Input type="date" value={params.date_from} onChange={set("date_from")} className="h-10 w-[150px] border-neutral-800 bg-neutral-900" data-testid="filter-date-from" />
          to
          <Input type="date" value={params.date_to} onChange={set("date_to")} className="h-10 w-[150px] border-neutral-800 bg-neutral-900" data-testid="filter-date-to" />
        </div>
      </div>
      <p className="mb-2 text-xs text-neutral-500" data-testid={`records-${name}-count`}>{loading ? "Loading…" : `${items.length} record(s)`}</p>
      <div className="overflow-x-auto rounded-lg border border-neutral-800">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="bg-neutral-900 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              {columns.map((c) => <th key={c.key} className="px-3 py-3 font-semibold">{c.label}</th>)}
              <th className="px-3 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-t border-neutral-900 align-top transition-colors hover:bg-neutral-900/60" data-testid={`row-${name}-${row.id}`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-3 text-neutral-300">{c.render ? c.render(row) : row[c.key]}</td>
                ))}
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {actions && actions(row, reload)}
                    <ConfirmDelete onConfirm={() => remove(row.id)} testid={`delete-${name}-${row.id}`} />
                  </div>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="px-3 py-10 text-center text-neutral-500" data-testid={`records-${name}-empty`}>No records match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
